import { useState, useEffect, useMemo } from 'react';
import { useImageOptimization } from '../context/ImageOptimizationContext';

/**
 * Custom hook for managing event images in the registration flow
 * 
 * Features:
 * - Generates optimized image URLs based on device capabilities
 * - Creates low-quality placeholders for faster loading
 * - Preloads critical images
 * - Tracks loading state of images
 * 
 * @param {Object} event - The event object containing image URLs
 * @param {boolean} shouldPreload - Whether to preload images
 * @returns {Object} - Object containing optimized image URLs and loading states
 */
const useEventImages = (event, shouldPreload = true) => {
  const { getOptimizedSrc, generatePlaceholder } = useImageOptimization();
  const [placeholders, setPlaceholders] = useState({});
  const [loadingStates, setLoadingStates] = useState({});
  
  // Get important image sources from event
  const imageSources = useMemo(() => {
    if (!event) return {};
    
    // Extract all image URLs from event object
    return {
      bannerImage: event.bannerImage || event.coverImage || event.heroImage,
      logoImage: event.logoImage || event.logo,
      venueImage: event.venueImage || (event.venue && event.venue.image),
      organizerLogo: event.organizerLogo || (event.organizer && event.organizer.logo)
    };
  }, [event]);
  
  // Generate optimized URLs
  const optimizedImages = useMemo(() => {
    if (!event) return {};
    
    const result = {};
    
    // Process each image source
    Object.entries(imageSources).forEach(([key, src]) => {
      if (src) {
        // Get optimized version based on image purpose
        if (key === 'bannerImage') {
          result[key] = getOptimizedSrc(src, 1200); // Wider for banners
        } else if (key === 'logoImage' || key === 'organizerLogo') {
          result[key] = getOptimizedSrc(src, 300); // Smaller for logos
        } else {
          result[key] = getOptimizedSrc(src, 800); // Default size
        }
      }
    });
    
    return result;
  }, [imageSources, getOptimizedSrc]);
  
  // Generate placeholders for each image
  useEffect(() => {
    const generatePlaceholders = async () => {
      const newPlaceholders = {};
      
      // Process each optimized image
      for (const [key, src] of Object.entries(optimizedImages)) {
        if (src) {
          try {
            setLoadingStates(prev => ({ ...prev, [key]: true }));
            const placeholder = await generatePlaceholder(src);
            if (placeholder) {
              newPlaceholders[key] = placeholder;
            }
          } catch (error) {
            console.warn(`Failed to generate placeholder for ${key}:`, error);
          } finally {
            setLoadingStates(prev => ({ ...prev, [key]: false }));
          }
        }
      }
      
      setPlaceholders(newPlaceholders);
    };
    
    if (event && Object.keys(optimizedImages).length > 0) {
      generatePlaceholders();
    }
  }, [optimizedImages, generatePlaceholder, event]);
  
  // Preload critical images
  useEffect(() => {
    if (!shouldPreload || !event) return;
    
    // Preload function
    const preloadImage = (src) => {
      return new Promise((resolve) => {
        if (!src) {
          resolve();
          return;
        }
        
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve(); // Still resolve on error to continue
        img.src = src;
      });
    };
    
    // Preload critical images in order of importance
    const preloadCriticalImages = async () => {
      const criticalImages = [
        optimizedImages.bannerImage,
        optimizedImages.logoImage
      ].filter(Boolean);
      
      for (const src of criticalImages) {
        await preloadImage(src);
      }
    };
    
    preloadCriticalImages();
  }, [event, optimizedImages, shouldPreload]);
  
  return {
    optimizedImages,
    placeholders,
    loadingStates,
    isLoading: Object.values(loadingStates).some(state => state)
  };
};

export default useEventImages; 